import type {Cloneable} from "@bindoc-projects/util-domain-lib";
import {AYS_CODE_DRG_INEK_SECTIONS} from "./index";

export class AysCodeDrgInekTable implements Cloneable<AysCodeDrgInekTable> {
  /**
   * The departments used in given data (${AYS_CODE_DRG_INEK_DEPARTMENTS} is the full list).
   */
  public departments: string[];

  /**
   * Summed section values für each department
   */
  public departmentSums: { [department: string]: number };

  /**
   * Summed department values für each section
   */
  public sectionSums: { [section: string]: number };

  public topSectionSums: { [topSection: string]: number };

  /**
   * total value of inek table, calculated through sum of departmentSums
   */
  public total: number;

  constructor(
    public data: { [department: string]: { [section: string]: number } }
  ) {
    this.departments = Object.keys(this.data);

    this._calculateDepartmentSums();
    this._calculateSectionSums();
    this._calculateTopSectionSums();

    this._calculateTotal();
  }

  public isEmpty(): boolean {
    return Object.keys(this.data).length === 0;
  }

  public clone(): AysCodeDrgInekTable {
    return new AysCodeDrgInekTable(JSON.parse(JSON.stringify(this.data)));
  }

  private _calculateDepartmentSums() {
    const departmentSums: { [department: string]: number } = {};

    this.departments.forEach((department) => {
      const sections = Object.keys(this.data[department]);
      let departmentCost = 0;

      if (this.data[department]) {
        sections.forEach((section) => {
          if (this.data[department][section]) {
            departmentCost = departmentCost + this.data[department][section];
          }
        });
      }

      departmentSums[department] = departmentCost;
    });

    this.departmentSums = departmentSums;
  }

  private _calculateTopSectionSums() {
    const staffSum =
      this.sectionSums['MEDICAL_SERVICE'] +
      this.sectionSums['MEDTECH_SERVICE'] +
      this.sectionSums['NURSING_SERVICE'];

    const drugSum =
      this.sectionSums['DRUGS_OVERHEAD'] + this.sectionSums['DRUGS_SINGLE'];

    const implantSum = this.sectionSums['IMPLANTS'];

    const medicalSuppliesSum =
      this.sectionSums['MEDICAL_SUPPLIES_OVERHEAD'] +
      this.sectionSums['MEDICAL_SUPPLIES_SINGLE'] +
      this.sectionSums['MEDICAL_SUPPLIES_EXTERNAL'];

    const infrastructureSum =
      this.sectionSums['INFRASTRUCTURE_MEDICAL'] +
      this.sectionSums['INFRASTRUCTURE_NON_MEDICAL'];

    const topSectionsSums: { [section: string]: number } = {
      STAFF: staffSum,
      DRUGS: drugSum,
      IMPLANTS: implantSum,
      MEDICAL_SUPPLIES: medicalSuppliesSum,
      INFRASTRUCTURE: infrastructureSum,
    };

    this.topSectionSums = topSectionsSums;
  }

  private _calculateSectionSums() {
    const sectionsSums: { [section: string]: number } = {};

    AYS_CODE_DRG_INEK_SECTIONS.forEach((costSection) => {
      let sectionCost = 0;

      this.departments.forEach((department) => {
        sectionCost = sectionCost + this.data[department][costSection];
      });

      sectionsSums[costSection] = sectionCost;
    });

    this.sectionSums = sectionsSums;
  }

  private _calculateTotal() {
    const sumFnc = (previousValue: number, currentValue: string) => {
      return previousValue + this.departmentSums[currentValue];
    };

    this.total = this.departments.reduce(sumFnc, 0);
  }
}
